import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import { Upload, X, FileIcon, Image, Loader2, Trash2, ExternalLink } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FileUpload({ 
  entityType = null, 
  entityId = null, 
  folder = "",
  onUpload = null,
  onDelete = null,
  accept = "image/*,application/pdf,.doc,.docx,.xls,.xlsx",
  maxSize = 10 * 1024 * 1024, // 10MB default
  multiple = false,
  showList = true,
  className = ""
}) {
  var { token } = useAuth();
  var { t } = useTranslation();
  var fileInputRef = useRef(null);
  
  var [uploading, setUploading] = useState(false);
  var [files, setFiles] = useState([]);
  var [loadingFiles, setLoadingFiles] = useState(false);
  var [storageConfigured, setStorageConfigured] = useState(null);

  // Check storage config and load files on mount
  useEffect(function() {
    checkStorageConfig();
    if (showList && entityId) {
      loadFiles();
    }
  }, [entityId]);

  async function checkStorageConfig() {
    try {
      var response = await fetch(API_URL + "/api/storage/config", {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });
      if (response.ok) {
        var data = await response.json();
        setStorageConfigured(data.configured);
      }
    } catch (err) {
      console.error("Error checking storage config:", err);
      setStorageConfigured(false);
    }
  }

  async function loadFiles() {
    if (!entityType || !entityId) return;
    
    setLoadingFiles(true);
    try {
      var url = API_URL + "/api/storage/files?entity_type=" + entityType + "&entity_id=" + entityId;
      var response = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });
      if (response.ok) {
        var data = await response.json();
        setFiles(data);
      }
    } catch (err) {
      console.error("Error loading files:", err);
    } finally {
      setLoadingFiles(false);
    }
  }

  async function handleFileSelect(e) {
    var selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validate file sizes
    var oversized = selectedFiles.filter(function(f) { return f.size > maxSize; });
    if (oversized.length > 0) {
      toast.error(t("fileUpload.fileTooLarge", { maxSize: Math.round(maxSize / 1024 / 1024) + "MB" }));
      return;
    }

    setUploading(true);
    
    for (var file of selectedFiles) {
      try {
        var formData = new FormData();
        formData.append("file", file);
        if (entityType) formData.append("entity_type", entityType);
        if (entityId) formData.append("entity_id", entityId);
        if (folder) formData.append("folder", folder);

        var response = await fetch(API_URL + "/api/storage/upload", {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: "Bearer " + token } : {},
          body: formData
        });

        if (response.ok) {
          var result = await response.json();
          toast.success(t("fileUpload.uploaded", { name: file.name }));
          
          if (onUpload) {
            onUpload(result);
          }
          
          // Reload file list
          if (showList && entityId) {
            loadFiles();
          }
        } else {
          var error = await response.json();
          toast.error(error.detail || t("fileUpload.uploadFailed"));
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(t("fileUpload.uploadFailed"));
      }
    }

    setUploading(false);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function handleDelete(fileId) {
    try {
      var response = await fetch(API_URL + "/api/storage/files/" + fileId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {}
      });

      if (response.ok) {
        toast.success(t("fileUpload.deleted"));
        setFiles(files.filter(function(f) { return f.file_id !== fileId; }));
        
        if (onDelete) {
          onDelete(fileId);
        }
      } else {
        toast.error(t("fileUpload.deleteFailed"));
      }
    } catch (err) {
      toast.error(t("fileUpload.deleteFailed"));
    }
  }

  function getFileIcon(contentType) {
    if (contentType && contentType.startsWith("image/")) {
      return <Image className="w-8 h-8 text-blue-500" />;
    }
    return <FileIcon className="w-8 h-8 text-gray-500" />;
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
  }

  if (storageConfigured === false) {
    return (
      <Card className={className}>
        <CardContent className="py-6 text-center">
          <Upload className="w-10 h-10 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">{t("fileUpload.notConfigured")}</p>
          <p className="text-xs text-gray-400 mt-1">{t("fileUpload.configureInSettings")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className}>
      {/* Upload Area */}
      <Card className="border-dashed">
        <CardContent className="py-6">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          
          <div 
            className="flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-4 transition-colors"
            onClick={function() { if (!uploading) fileInputRef.current?.click(); }}
          >
            {uploading ? (
              <>
                <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-2" />
                <p className="text-sm text-gray-500">{t("fileUpload.uploading")}</p>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                <p className="text-sm font-medium">{t("fileUpload.clickToUpload")}</p>
                <p className="text-xs text-gray-400">{t("fileUpload.maxSize", { size: Math.round(maxSize / 1024 / 1024) + "MB" })}</p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* File List */}
      {showList && (
        <div className="mt-4 space-y-2">
          {loadingFiles ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : files.length > 0 ? (
            files.map(function(file) {
              return (
                <div 
                  key={file.file_id}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  data-testid={"file-item-" + file.file_id}
                >
                  {file.content_type && file.content_type.startsWith("image/") && file.file_url ? (
                    <img 
                      src={file.file_url} 
                      alt={file.file_name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    getFileIcon(file.content_type)
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.file_name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.file_size)}</p>
                  </div>
                  
                  <div className="flex gap-1">
                    {file.file_id && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={function() { window.open(API_URL + "/api/storage/download/" + file.file_id, "_blank"); }}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={function() { handleDelete(file.file_id); }}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center text-sm text-gray-400 py-4">{t("fileUpload.noFiles")}</p>
          )}
        </div>
      )}
    </div>
  );
}
